import { lazy, type ComponentType } from "react";

const RELOAD_FLAG = "tm-chunk-reload-attempted";

/**
 * Wraps React.lazy with auto-recovery for the "stale chunk" problem.
 *
 * When we deploy, the index bundle a user already has open references
 * Onboarding-OLDHASH.js. The new deploy serves Onboarding-NEWHASH.js
 * and the OLD chunk no longer exists. Vercel's catch-all SPA fallback
 * returns index.html for unknown paths, so the dynamic import resolves
 * to text/html which the browser rejects with:
 *   "Failed to load module script: Expected a JavaScript-or-Wasm
 *    module script but the server responded with a MIME type of
 *    text/html. Strict MIME type checking is enforced…"
 *
 * Recovery: hard-reload once. The fresh index.html will reference the
 * new chunk hashes. We gate on a sessionStorage flag so we don't get
 * stuck in a reload loop if the failure is actually a real network
 * issue (in that case the second failure surfaces to ErrorBoundary).
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): ReturnType<typeof lazy<T>> {
  return lazy(async () => {
    try {
      const mod = await factory();
      // Successful load — clear any prior reload flag so future deploys
      // can trigger their own recovery.
      sessionStorage.removeItem(RELOAD_FLAG);
      return mod;
    } catch (err: any) {
      const msg = String(err?.message ?? err);
      const isChunkLoadError =
        msg.includes("Failed to fetch dynamically imported module") ||
        msg.includes("Failed to load module script") ||
        msg.includes("error loading dynamically imported module") ||
        msg.includes("Importing a module script failed");

      if (isChunkLoadError && !sessionStorage.getItem(RELOAD_FLAG)) {
        sessionStorage.setItem(RELOAD_FLAG, "1");
        // Hard reload: discards stale in-memory bundle, fetches the
        // new index.html with the current chunk-hash references.
        window.location.reload();
        // Return a never-resolving promise so React's Suspense fence
        // stays in the loading state while the reload happens.
        return new Promise(() => {}) as Promise<{ default: T }>;
      }
      throw err;
    }
  });
}
